import { useRegistrationAccessesQuery } from "@pathwright/graphql-client/src/generated"
import { flattenEdges } from "@pathwright/utils"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMountedState } from "react-use"
import {
  getActiveSubscriptionIdChanges,
  useLastHeartbeatVar
} from "../../auth/heartbeat/heartbeat"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { ORDER_FULFILLED_EVENT } from "../../utils/dispatcher"
import {
  getNearestAccessStateValidUntil,
  indexRegistrationAccesses,
  type RegistrationAccessIndex,
  type RegistrationAccessRow
} from "./selectors"

export type RegistrationAccessStoreValue = {
  rows: RegistrationAccessRow[]
  index: RegistrationAccessIndex
  loading: boolean
  error: Error | undefined
  /** A complete successful access map is available, including during refresh. */
  ready: boolean
  refetch: () => Promise<unknown>
}

const EMPTY_ROWS: RegistrationAccessRow[] = []
const EMPTY_INDEX = indexRegistrationAccesses(EMPTY_ROWS)
const REGISTRATION_ACCESSES_QUERY_VARIABLES = {
  first: 1042
} as const

/** Loads all access pages and owns the provider's freshness side effects. */
export function useRegistrationAccessStore(): RegistrationAccessStoreValue {
  const { me, school } = usePathwrightContext()
  const signedIn = Boolean(me)
  const [paginationError, setPaginationError] = useState<Error>()
  // fetchMore updates query.loading while the loop is running.
  const fetchingMore = useRef(false)
  const isMounted = useMountedState()

  const query = useRegistrationAccessesQuery({
    variables: REGISTRATION_ACCESSES_QUERY_VARIABLES,
    skip: !signedIn,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true
  })

  const refetch = useCallback(async () => {
    if (!signedIn) return
    setPaginationError(undefined)
    try {
      return await query.refetch({
        ...REGISTRATION_ACCESSES_QUERY_VARIABLES,
        after: null
      })
    } catch (error) {
      if (isMounted()) setPaginationError(error as Error)
    }
  }, [query.refetch, signedIn, isMounted])

  const pageInfo = query.data?.me?.registration_accesses?.pageInfo
  useEffect(() => {
    if (
      !signedIn ||
      query.loading ||
      query.error ||
      fetchingMore.current ||
      paginationError ||
      !pageInfo?.hasNextPage
    )
      return

    fetchingMore.current = true
    const loadRemainingPages = async () => {
      const seenCursors = new Set<string>()
      let nextPage = pageInfo
      while (isMounted() && nextPage.hasNextPage) {
        const cursor = nextPage.endCursor
        if (!cursor || seenCursors.has(cursor)) {
          throw new Error(
            "Registration access pagination did not advance its cursor."
          )
        }
        seenCursors.add(cursor)
        const { data } = await query.fetchMore({ variables: { after: cursor } })
        if (!isMounted()) return
        const connection = data.me?.registration_accesses
        if (!connection) throw new Error("Registration access page is missing.")
        nextPage = connection.pageInfo
      }
    }
    void loadRemainingPages()
      .catch((error: Error) => {
        if (isMounted()) setPaginationError(error)
      })
      .finally(() => {
        fetchingMore.current = false
      })
  }, [
    signedIn,
    isMounted,
    query.loading,
    query.error,
    query.fetchMore,
    pageInfo,
    paginationError
  ])

  const rows = useMemo(
    () => flattenEdges(query.data?.me?.registration_accesses) ?? EMPTY_ROWS,
    [query.data?.me?.registration_accesses]
  )

  const index = useMemo(() => indexRegistrationAccesses(rows), [rows])

  const heartbeat = useLastHeartbeatVar()

  // Heartbeat: refetch once when active subscription IDs change.
  const prevSubscriptionIdsRef = useRef<number[] | undefined>()
  useEffect(() => {
    if (!signedIn) {
      prevSubscriptionIdsRef.current = undefined
      return
    }

    const currentIds = heartbeat.active_subscription_ids
    const previousIds = prevSubscriptionIdsRef.current
    prevSubscriptionIdsRef.current = currentIds

    const changes = getActiveSubscriptionIdChanges(previousIds, currentIds)
    if (changes && (changes.added.length > 0 || changes.removed.length > 0)) {
      void refetch()
    }
  }, [signedIn, heartbeat.active_subscription_ids, refetch])

  // Order fulfillment: any fulfilled order may create a registration.
  useEffect(() => {
    if (!signedIn) return

    const onOrderFulfilled = () => {
      void refetch()
    }
    document.addEventListener(ORDER_FULFILLED_EVENT, onOrderFulfilled)
    return () => {
      document.removeEventListener(ORDER_FULFILLED_EVENT, onOrderFulfilled)
    }
  }, [signedIn, refetch])

  // Schedule one refetch just after the nearest known access-state boundary.
  const nearestBoundary = useMemo(
    () => getNearestAccessStateValidUntil(rows),
    [rows]
  )
  const nearestBoundaryMs = nearestBoundary?.getTime() ?? null

  useEffect(() => {
    if (nearestBoundaryMs == null) return

    const delay = Math.max(0, nearestBoundaryMs - Date.now()) + 250
    const timeoutId = window.setTimeout(() => {
      void refetch()
    }, delay)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [nearestBoundaryMs, refetch])

  // If the tab slept through a boundary, refetch on return.
  useEffect(() => {
    if (nearestBoundaryMs == null) return

    const maybeRefetchIfBoundaryPassed = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() >= nearestBoundaryMs
      ) {
        void refetch()
      }
    }

    document.addEventListener("visibilitychange", maybeRefetchIfBoundaryPassed)
    window.addEventListener("focus", maybeRefetchIfBoundaryPassed)
    return () => {
      document.removeEventListener(
        "visibilitychange",
        maybeRefetchIfBoundaryPassed
      )
      window.removeEventListener("focus", maybeRefetchIfBoundaryPassed)
    }
  }, [nearestBoundaryMs, refetch])

  const completed = useRef<{
    userId: number
    schoolId: number
    rows: RegistrationAccessRow[]
    index: RegistrationAccessIndex
  } | null>(null)
  if (
    completed.current?.userId !== me?.id ||
    completed.current?.schoolId !== school?.id
  )
    completed.current = null

  if (!signedIn) {
    return {
      rows: EMPTY_ROWS,
      index: EMPTY_INDEX,
      loading: false,
      error: undefined,
      ready: false,
      refetch
    }
  }

  const error =
    query.error ||
    paginationError ||
    (!query.data?.me?.registration_accesses && !query.loading
      ? new Error("Registration access page is missing.")
      : undefined)
  const loading = query.loading || (!!pageInfo?.hasNextPage && !error)
  const ready =
    query.data?.me?.id === me.id &&
    !!pageInfo &&
    !pageInfo.hasNextPage &&
    !loading &&
    !error
  if (ready)
    completed.current = { userId: me.id, schoolId: school.id, rows, index }
  // A refresh is not a loss of access. Keep the last complete map while the
  // first page (or remaining pages) reloads, so Continue never flashes Join.
  // Initial loads and failures remain unready; never carry access across users.
  const refreshing = loading && !error ? completed.current : null
  return {
    rows: refreshing?.rows ?? rows,
    index: refreshing?.index ?? index,
    loading,
    error,
    ready: ready || !!refreshing,
    refetch
  }
}
